import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { AssetOverviewService } from '../../../services/asset-overview.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { ErrorStateComponent } from '../../../../../_shared/components/states/error-state/error-state.component';
import {
  AssetInstalledProductResponse,
  InstalledProductCard,
  InstalledProductDetails,
  InstalledProductKey
} from '../../../models/asset-overview-response.model';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

const PRODUCT_ORDER: InstalledProductKey[] = ['FuelProbes', 'DriverAi', 'Camera', 'Tracking'];
const MAX_CAMERAS = 8;

@Component({
  selector: 'app-globaltrack-products-modal',
  standalone: true,
  imports: [CommonModule, ErrorStateComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './globaltrack-products-modal.component.html',
  styleUrl: './globaltrack-products-modal.component.scss'
})
export class GlobaltrackProductsModalComponent implements OnInit, OnDestroy {
  @Input({ required: true }) assetId!: number;

  isLoading: boolean = true;
  hasError: boolean = false;
  errorMessage: string = '';
  isSendingRequest: boolean = false;
  productCards: InstalledProductCard[] = [];
  selectedCard: InstalledProductCard | null = null;
  selectedDetails: InstalledProductDetails | null = null;

  private activeModal: NgbActiveModal = inject(NgbActiveModal);
  private assetOverviewService = inject(AssetOverviewService);
  private errorHandlerService = inject(ErrorHandlerService);
  private toastr = inject(ToastrService);
  private readonly transloco: TranslocoService = inject(TranslocoService);

  private readonly cancelSubject = new Subject<void>();

  ngOnInit(): void {
    void this.loadInstalledProductsAsync();
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  close(): void {
    this.activeModal.dismiss();
  }

  openProductDetails(card: InstalledProductCard): void {
    this.selectedCard = card;
    this.selectedDetails = { keyFeatures: card.keyFeatures, installation: card.installation };
  }

  backToProducts(): void {
    this.selectedCard = null;
    this.selectedDetails = null;
  }

  async requestSelectedProductAsync(): Promise<void> {
    if (!this.selectedCard) {
      return;
    }

    const requestType = this.selectedCard.key === 'FuelProbes' && this.selectedCard.isInstalled ? 'More' : 'Installation';
    await this.sendProductRequestAsync(this.selectedCard.key, requestType);
  }

  async requestUpgradesAsync(): Promise<void> {
    await this.sendProductRequestAsync('All Products', 'Upgrade');
  }

  private async sendProductRequestAsync(product: string, requestType: string): Promise<void> {
    this.isSendingRequest = true;

    try {
      await this.assetOverviewService.sendProductRequest(
        { assetId: this.assetId, product, requestType },
        this.cancelSubject);
      this.toastr.success(
        this.transloco.translate('assetOverview.products.toasts.requestSent'),
        this.transloco.translate('assetOverview.products.toasts.requestSentTitle'));
      this.isSendingRequest = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: (message) => {
          this.toastr.error(message || this.transloco.translate('assetOverview.products.toasts.requestFailed'));
          this.isSendingRequest = false;
        }
      });
    }
  }

  private async loadInstalledProductsAsync(): Promise<void> {
    this.hasError = false;
    this.isLoading = true;
    this.errorMessage = '';

    try {
      const response = await this.assetOverviewService.getAssetInstalledProducts({ assetId: this.assetId }, this.cancelSubject);
      this.productCards = this.mapToCards(response?.data ?? []);
      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: (message) => {
          this.errorMessage = message;
          this.hasError = true;
          this.isLoading = false;
        }
      });
    }
  }

  private mapToCards(products: AssetInstalledProductResponse[]): InstalledProductCard[] {
    return PRODUCT_ORDER.map(key => {
      const product = products.find(p => p.product === key);
      const quantity = product?.quantity ?? 0;
      const isInstalled = product?.isInstalled ?? false;

      return {
        key,
        title: product?.unitTypes || product?.title || key,
        description: product?.description ?? '',
        quantity,
        isInstalled,
        actionLabel: this.getActionLabel(key, isInstalled),
        actionEnabled: this.isActionEnabled(key, isInstalled, quantity),
        keyFeatures: product?.keyFeatures ?? [],
        installation: product?.installation ?? null
      };
    });
  }

  private getActionLabel(key: InstalledProductKey, isInstalled: boolean): string {
    return key === 'FuelProbes' && isInstalled
      ? this.transloco.translate('assetOverview.products.requestMore')
      : this.transloco.translate('assetOverview.products.requestInstallation');
  }

  private isActionEnabled(key: InstalledProductKey, isInstalled: boolean, quantity: number): boolean {
    if (key === 'Camera') {
      return quantity < MAX_CAMERAS;
    }

    return key === 'FuelProbes' || !isInstalled;
  }
}
